export type DimensionKey =
  | 'readerIntentCapture'
  | 'guidedApplication'
  | 'ownedAuthority'
  | 'businessSignalRouting'

export interface ScoredOption {
  letter: 'A' | 'B' | 'C' | 'D'
  text: string
  score: 0 | 1 | 2 | 3
}

export interface ScoredQuestion {
  id: string
  dimension: DimensionKey
  prompt: string
  options: ScoredOption[]
}

export type ScoredAnswers = Record<string, number>

export type ScoreBand =
  | 'Trust Is Being Created, But the Path Is Still Passive'
  | 'Useful Pieces, But Not a Connected Path'
  | 'Your Book Is Partially Activated'
  | 'Your Book Is Activation-Ready'
  | 'Your Book Is Already Working Hard'

export interface ScoreResult {
  totalScore: number
  dimensions: Record<DimensionKey, number>
  band: ScoreBand
  biggestOpportunity: DimensionKey
  strongestLayer: DimensionKey
}

export const DIMENSION_ORDER: DimensionKey[] = [
  'readerIntentCapture',
  'guidedApplication',
  'ownedAuthority',
  'businessSignalRouting',
]

export const DIMENSION_LABEL: Record<DimensionKey, string> = {
  readerIntentCapture: 'Reader Intent Capture',
  guidedApplication: 'Guided Application',
  ownedAuthority: 'Owned Authority',
  businessSignalRouting: 'Business Signal + Routing',
}

export function dimensionLevel(score: number) {
  if (score >= 6) return 'Strong'
  if (score >= 4) return 'Working'
  if (score >= 2) return 'Emerging'
  return 'Underbuilt'
}

const optionSet = (texts: [string, string, string, string]): ScoredOption[] =>
  texts.map((text, index) => ({
    letter: ['A', 'B', 'C', 'D'][index] as ScoredOption['letter'],
    text,
    score: index as 0 | 1 | 2 | 3,
  }))

export const SCORED_QUESTIONS: ScoredQuestion[] = [
  {
    id: 'q1.intent.nextAsk',
    dimension: 'readerIntentCapture',
    prompt:
      'A reader hears you on a podcast, skims a chapter, or lands on your site after seeing the book. What do you ask them to do next?',
    options: optionSet([
      'Buy the book, follow me, visit my site, or reach out if they want.',
      'Download a resource, join my newsletter, or check out a bonus page.',
      'Choose from a few useful next steps, depending on what they are looking for.',
      'Start a guided path that helps them identify where they are and what fits next.',
    ]),
  },
  {
    id: 'q2.intent.handRaiserContext',
    dimension: 'readerIntentCapture',
    prompt:
      'When someone raises their hand after engaging with the book, what do you actually know about them?',
    options: optionSet([
      'Usually nothing, or just that they bought, read, or mentioned the book.',
      'I may get their name, email, or a general inquiry.',
      'I can usually see the topic, resource, or problem they were interested in.',
      'I can see their situation, what they care about, and what next step may fit.',
    ]),
  },
  {
    id: 'q3.apply.selfLocate',
    dimension: 'guidedApplication',
    prompt:
      'Can a reader find themselves in the book without reading the whole thing front to back?',
    options: optionSet([
      'Not really. They have to interpret the book on their own.',
      'Sort of. I have worksheets or resources, but the reader still does most of the sorting.',
      'In some places. A few ideas, chapters, or tools help them self-identify.',
      'Yes. They can recognize their pattern, find the right concept, and follow a useful path.',
    ]),
  },
  {
    id: 'q4.apply.thisWeek',
    dimension: 'guidedApplication',
    prompt:
      'After someone engages with the book, do they leave with something useful they can do this week?',
    options: optionSet([
      'Not usually.',
      'Sometimes. I give general advice, exercises, or resources.',
      'Yes, but the next move is mostly the same for everyone.',
      'Yes. Their next move changes based on their situation or readiness.',
    ]),
  },
  {
    id: 'q5.authority.shapesCampaigns',
    dimension: 'ownedAuthority',
    prompt:
      'When you create a new campaign, webinar, lead magnet, post, or sales conversation, how often does the book shape it?',
    options: optionSet([
      'Rarely. The book mostly sits apart from my current marketing or sales motion.',
      'Sometimes. I reference the book, but new campaigns often get built separately.',
      'Often. The book gives me topics, language, stories, or frameworks to use.',
      "Consistently. The book's concepts are mapped into campaigns, content, talks, CTAs, and offer paths.",
    ]),
  },
  {
    id: 'q6.authority.sendThemWhere',
    dimension: 'ownedAuthority',
    prompt:
      'A post, podcast, keynote, or interview gets someone interested in one specific book idea. Where can you send them?',
    options: optionSet([
      'Mostly back to the book, my profile, or my main website.',
      'To a newsletter, resource page, or general opt-in.',
      'To a relevant article, bonus, resource, or page connected to that idea.',
      'To a specific owned path where they can apply that idea and take the right next step.',
    ]),
  },
  {
    id: 'q7.routing.strongFitReader',
    dimension: 'businessSignalRouting',
    prompt:
      'A strong-fit reader is interested in paid work. How do they get pointed in the right direction?',
    options: optionSet([
      'They have to figure out how to contact me.',
      'They use a general contact form or book-a-call link.',
      'I point them toward one or two relevant offers.',
      'Their situation routes them toward the right offer, conversation, or application path.',
    ]),
  },
  {
    id: 'q8.routing.demandSignal',
    dimension: 'businessSignalRouting',
    prompt: 'What do you know about demand forming behind the book?',
    options: optionSet([
      'Very little beyond book sales, traffic, likes, or comments.',
      'I can see basic downloads, email signups, or inquiries.',
      'I can see some topics, resources, or reader behaviors that get interest.',
      'I can see patterns: which concepts matter, what readers need, and where qualified interest is forming.',
    ]),
  },
]

export const TOTAL_QUESTIONS = SCORED_QUESTIONS.length

const opportunityPriority: DimensionKey[] = [
  'businessSignalRouting',
  'guidedApplication',
  'readerIntentCapture',
  'ownedAuthority',
]

export const BAND_CONTENT: Record<
  ScoreBand,
  {
    acknowledgment: string
    meaning: string
    opportunity: string
    nextStep: string
    ctaHelper: string
  }
> = {
  'Trust Is Being Created, But the Path Is Still Passive': {
    acknowledgment:
      'Your book may already be creating credibility. That matters.',
    meaning:
      'The ideas may be strong, but interested readers are probably being sent to generic destinations.',
    opportunity:
      'Build a clearer reader path so the right person can self-locate and take a useful next step.',
    nextStep: 'Start with reader intent capture and guided application.',
    ctaHelper:
      'A review can clarify what needs to exist before a build makes sense.',
  },
  'Useful Pieces, But Not a Connected Path': {
    acknowledgment:
      'You likely have some useful assets around the book already.',
    meaning:
      'Resources and next steps may exist, but they may not yet connect into a coherent reader path.',
    opportunity:
      'Connect the existing pieces into a clearer reader journey and identify where intent leaks.',
    nextStep:
      'Map the current reader path and find the first disconnected layer.',
    ctaHelper: 'This is a fit conversation, not a hard pitch.',
  },
  'Your Book Is Partially Activated': {
    acknowledgment: 'You are not starting from zero.',
    meaning:
      'Your book likely has useful pathways and business relevance already.',
    opportunity: 'Improve the active business layer behind the book.',
    nextStep:
      'Review where the book can better support content, follow-up, and routing.',
    ctaHelper:
      'This is likely a strong-fit conversation if you have a real business behind the book.',
  },
  'Your Book Is Activation-Ready': {
    acknowledgment: 'You have a strong foundation.',
    meaning:
      'The book likely creates trust and already has meaningful assets around it.',
    opportunity:
      'Optimize the system for clearer routing and higher-value opportunities.',
    nextStep:
      'Review routing, signal, premium offer paths, and handoff into the business.',
    ctaHelper:
      'Position this as optimization and expansion, not fixing the book.',
  },
  'Your Book Is Already Working Hard': {
    acknowledgment: 'You are doing a lot right.',
    meaning:
      'Your book appears to have a meaningful path and connection to your business.',
    opportunity: 'Look for leverage.',
    nextStep:
      'Explore segmentation, stronger fit, premium paths, and smoother sales handoff.',
    ctaHelper:
      'This may be an optimization conversation rather than a full rebuild.',
  },
}

export function computeScore(answers: ScoredAnswers): ScoreResult {
  const dimensions: Record<DimensionKey, number> = {
    readerIntentCapture: 0,
    guidedApplication: 0,
    ownedAuthority: 0,
    businessSignalRouting: 0,
  }

  const totalScore = SCORED_QUESTIONS.reduce((total, question) => {
    const value = answers[question.id] ?? 0
    dimensions[question.dimension] += value
    return total + value
  }, 0)

  const minScore = Math.min(...DIMENSION_ORDER.map((key) => dimensions[key]))
  const maxScore = Math.max(...DIMENSION_ORDER.map((key) => dimensions[key]))

  return {
    totalScore,
    dimensions,
    band:
      totalScore >= 22
        ? 'Your Book Is Already Working Hard'
        : totalScore >= 18
          ? 'Your Book Is Activation-Ready'
          : totalScore >= 12
            ? 'Your Book Is Partially Activated'
            : totalScore >= 6
              ? 'Useful Pieces, But Not a Connected Path'
              : 'Trust Is Being Created, But the Path Is Still Passive',
    biggestOpportunity:
      opportunityPriority.find((key) => dimensions[key] === minScore) ??
      'businessSignalRouting',
    strongestLayer:
      [...opportunityPriority]
        .reverse()
        .find((key) => dimensions[key] === maxScore) ?? 'ownedAuthority',
  }
}

export function getQuestion(questionId: string) {
  return SCORED_QUESTIONS.find((question) => question.id === questionId)
}
